import type * as React from "react";

/**
 * A coloured dot that sits beside a name to report liveness. Three states only:
 * `online` and `offline` are the two readings of a TTL presence hint, and
 * `revoked` is a lifecycle fact, not a liveness one — it is hollow rather than
 * filled so it never reads as "just offline".
 *
 * Colour alone is not the signal: pass `label` so the state is also words.
 */
export interface StatusDotProps {
  status: "online" | "offline" | "revoked";
  /** Visible text after the dot. Also becomes the accessible name. */
  label?: React.ReactNode;
  /** Accessible name when no visible `label` is rendered. */
  srLabel?: string;
  /** @default "md" */
  size?: "sm" | "md";
  style?: React.CSSProperties;
}

const COLORS: Record<StatusDotProps["status"], string> = {
  online: "var(--green-500)",
  offline: "var(--warm-400)",
  revoked: "var(--red-500)"
};

const DIAMETERS: Record<NonNullable<StatusDotProps["size"]>, number> = { sm: 7, md: 9 };

export function StatusDot({ status, label, srLabel, size = "md", style }: StatusDotProps) {
  const color = COLORS[status];
  const d = DIAMETERS[size];
  const hollow = status === "revoked";

  return (
    <span
      style={
        {
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontFamily: "var(--font-sans)",
          fontSize: size === "sm" ? "var(--text-micro)" : "var(--text-body-sm)",
          fontWeight: "var(--fw-semibold)",
          color: "var(--text-secondary)",
          lineHeight: 1.4,
          ...style
        } as React.CSSProperties
      }
    >
      <span
        role={label ? undefined : "img"}
        aria-hidden={label ? true : undefined}
        aria-label={label ? undefined : srLabel}
        style={{
          width: `${d}px`,
          height: `${d}px`,
          flex: "none",
          borderRadius: "50%",
          background: hollow ? "transparent" : color,
          border: `${hollow ? 2 : 1}px solid ${color}`,
          boxSizing: "border-box"
        }}
      />
      {label}
    </span>
  );
}
